import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { epicsApi, teamsApi, ticketsApi } from "../api/endpoints";
import { ApiError } from "../api/client";
import {
  STATE_LABELS,
  TICKET_STATES,
  TICKET_TYPES,
  TYPE_LABELS,
  type Comment,
  type Epic,
  type Team,
  type Ticket,
  type TicketState,
  type TicketType,
} from "../api/types";
import { EmptyState, ErrorBanner, Loading } from "../components/Feedback";
import { useConfirm } from "../components/ConfirmDialog";
import { Icon } from "../components/Icon";
import { Avatar } from "../components/Avatar";
import { formatDateTime, timeAgo } from "../utils/time";

export function TicketDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);

  const [type, setType] = useState<TicketType>("bug");
  const [state, setState] = useState<TicketState>("new");
  const [epicId, setEpicId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [commentBody, setCommentBody] = useState("");

  const syncForm = (t: Ticket) => {
    setType(t.type);
    setState(t.state);
    setEpicId(t.epicId ?? "");
    setTitle(t.title);
    setBody(t.body);
  };

  const load = async () => {
    setError(null);
    try {
      const t = await ticketsApi.get(id);
      setTicket(t);
      syncForm(t);
      const [ep, cs, tm] = await Promise.all([
        epicsApi.listByTeam(t.teamId).catch(() => [] as Epic[]),
        ticketsApi.listComments(id),
        teamsApi.list().catch(() => [] as Team[]),
      ]);
      setEpics(ep);
      setComments(cs);
      setTeams(tm);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load ticket.");
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const teamName = useMemo(
    () => teams.find((t) => t.id === ticket?.teamId)?.name ?? "Team",
    [teams, ticket],
  );

  const dirty =
    !!ticket &&
    (type !== ticket.type ||
      state !== ticket.state ||
      (epicId || null) !== (ticket.epicId ?? null) ||
      title !== ticket.title ||
      body !== ticket.body);

  const save = async () => {
    if (!ticket) return;
    setBusy(true);
    setError(null);
    try {
      let updated = ticket;
      if (
        type !== ticket.type ||
        (epicId || null) !== (ticket.epicId ?? null) ||
        title !== ticket.title ||
        body !== ticket.body
      ) {
        updated = await ticketsApi.update(ticket.id, {
          type,
          epicId: epicId || null,
          title: title.trim(),
          body,
        });
      }
      if (state !== ticket.state) {
        updated = await ticketsApi.changeState(ticket.id, state);
      }
      setTicket(updated);
      syncForm(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save changes.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!ticket) return;
    const ok = await confirm({
      title: "Delete ticket",
      message: "This deletes the ticket and all of its comments. This cannot be undone.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await ticketsApi.remove(ticket.id);
      navigate("/board", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete ticket.");
    }
  };

  const addComment = async () => {
    if (!ticket || !commentBody.trim()) return;
    setPosting(true);
    try {
      const c = await ticketsApi.addComment(ticket.id, commentBody.trim());
      setComments((prev) => [...(prev ?? []), c]);
      setCommentBody("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to add comment.");
    } finally {
      setPosting(false);
    }
  };

  if (error && !ticket) return <ErrorBanner message={error} />;
  if (!ticket) return <Loading label="Loading ticket…" />;

  return (
    <section className="page">
      <div className="detail-top">
        <div>
          <button className="link-button" onClick={() => navigate("/board")}>
            <Icon name="arrowLeft" size={16} /> Back to {teamName}
          </button>
          <div className="detail-meta">
            <span className="mono">#{ticket.id.slice(0, 8)}</span>
            <span className="dot">•</span>
            <span>by {ticket.createdByEmail}</span>
            <span className="dot">•</span>
            <span>Created {formatDateTime(ticket.createdAt)}</span>
            <span className="dot">•</span>
            <span>Modified {timeAgo(ticket.modifiedAt)}</span>
          </div>
          <h1 className="page-title" style={{ marginTop: 4 }}>
            {ticket.title}
          </h1>
        </div>
        <div className="row">
          <button className="icon-btn danger" onClick={remove} title="Delete ticket">
            <Icon name="trash" size={18} />
          </button>
          <button className="btn-primary" onClick={save} disabled={!dirty || busy || !title.trim()}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="detail-grid">
        <div className="card card-pad detail-form">
          <div className="detail-form-row">
            <label className="field">
              <span className="field-label">Team</span>
              <select value={ticket.teamId} disabled title="A ticket's team cannot be changed">
                <option value={ticket.teamId}>{teamName}</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">Type</span>
              <select value={type} onChange={(e) => setType(e.target.value as TicketType)}>
                {TICKET_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">State</span>
              <select value={state} onChange={(e) => setState(e.target.value as TicketState)}>
                {TICKET_STATES.map((s) => (
                  <option key={s} value={s}>
                    {STATE_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="field">
            <span className="field-label">Epic</span>
            <select value={epicId} onChange={(e) => setEpicId(e.target.value)}>
              <option value="">— None —</option>
              {epics.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field-label">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>

          <label className="field">
            <span className="field-label">Body</span>
            <textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} />
          </label>
        </div>

        <div className="card card-pad comments-panel">
          <div className="comments-head">
            <h2>Comments</h2>
            <span className="count-pill">{comments?.length ?? 0}</span>
          </div>

          {comments === null ? (
            <Loading />
          ) : comments.length === 0 ? (
            <EmptyState message="No comments yet." />
          ) : (
            <ul className="comment-list">
              {comments.map((c) => (
                <li key={c.id} className="comment">
                  <div className="comment-meta">
                    <Avatar name={c.createdByEmail} small />
                    <span className="who">{c.createdByEmail}</span>
                    <span className="when">{timeAgo(c.createdAt)}</span>
                  </div>
                  <p>{c.body}</p>
                </li>
              ))}
            </ul>
          )}

          <div className="comment-compose">
            <textarea
              rows={3}
              placeholder="Write a comment…"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
            />
            <button
              className="btn-primary"
              style={{ alignSelf: "flex-end" }}
              onClick={addComment}
              disabled={posting || !commentBody.trim()}
            >
              <Icon name="comment" size={16} /> {posting ? "Posting…" : "Post comment"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
